import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { env } from './env';
import { getIsAdmin, useAuth } from '../hooks/useAuth';

// Extend AxiosRequestConfig to include our custom property
interface CustomInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const api = axios.create({
  baseURL: env.API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
}[] = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null
) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    const accessToken = useAuth.getState().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomInternalAxiosRequestConfig;

    // Don't retry if:
    // 1. No original request exists
    // 2. It's already a retry
    // 3. It's a refresh token request
    // 4. It's a login request
    if (
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url === '/api/auth/refresh' ||
      originalRequest.url === '/api/auth/login'
    ) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      const isRefreshing = useAuth.getState().isRefreshing;

      if (isRefreshing) {
        try {
          const token = await new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      originalRequest._retry = true;
      useAuth.setState({ isRefreshing: true });

      try {
        const refreshResponse = await api.post('/api/auth/refresh');
        const { accessToken, user } = refreshResponse.data;

        // Update the Zustand store with new access token;
        useAuth.setState({
          accessToken,
          user,
          isRefreshing: false,
          isAdmin: getIsAdmin(accessToken),
        });

        api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        processQueue(null, accessToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);

        // Clear auth state and redirect to login
        useAuth.setState({
          accessToken: null,
          user: null,
          isAuthenticated: false,
          isRefreshing: false,
          isAdmin: false,
        });

        if (
          !window.location.pathname.includes('logout') &&
          !window.location.pathname.includes('/listing/')
        ) {
          window.location.href = `/auth/login?backTo=${encodeURIComponent(
            window.location.pathname + window.location.search
          )}`;
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
